import clsx from "clsx";
import { type ComponentProps, useCallback, useMemo } from "react";

import { Button } from "@shared/ui/button";
import { Icon } from "@shared/ui/icon";
import { Image } from "@shared/ui/image";
import { Body } from "@shared/ui/typography";
import { InViewVideo } from "@shared/ui/video/video";
import { mod } from "@shared/utils";

import type { MediaPostType } from "../model";

import s from "./media-card.module.scss";

export type MediaCardProps = ComponentProps<"div"> & {
  className?: string;
  isVideo: boolean;
  onCardClick?: (id: string) => void;
  post: MediaPostType;
};

export const MediaCard = (props: MediaCardProps) => {
  const {
    className,
    isVideo,
    onCardClick,
    post: { documentId, mediaContent },
  } = props;

  const handleClick = useCallback(() => {
    if (onCardClick) onCardClick(documentId);
  }, [onCardClick, documentId]);

  const iconColor = useMemo(() => {
    const iconType = mediaContent?.icon || "location";
    switch (iconType) {
      case "walk":
        return "violet";
      case "location":
      default:
        return "cadmium";
    }
  }, [mediaContent?.icon]);

  const mods = mod(s, { iconColor });

  /**
   * Внутри кнопки только медиа и, возможно, подпись с локацией — без имени
   * кнопка недоступна скринридеру. Локацию включаем в имя целиком: если она
   * видна на карточке, доступное имя обязано её содержать, иначе получаем
   * label-content-name-mismatch.
   */
  const buttonLabel = mediaContent?.location
    ? `${isVideo ? "Смотреть видео" : "Открыть фото"}: ${mediaContent.location}`
    : isVideo
      ? "Смотреть видео"
      : "Открыть фото";

  return (
    <article className={clsx(s.root, className, mods)}>
      <Button
        onClick={handleClick}
        className={s.wrapper}
        aria-label={buttonLabel}
      >
        {isVideo && (
          <div className={s.playButton}>
            <Icon name="play" />
          </div>
        )}
        <div className={s.filter} />
        {isVideo ? (
          mediaContent?.media?.url ? (
            <InViewVideo
              src={mediaContent.media.url}
              className={s.video}
              muted
            />
          ) : null
        ) : mediaContent?.media?.url ? (
          <Image
            src={mediaContent.media.url}
            alt={mediaContent.media?.alternativeText ?? "media card image"}
            className={s.media}
            height="100%"
          />
        ) : null}
        {(mediaContent?.location ?? mediaContent?.icon) && (
          <div className={s.location}>
            <Icon
              name={mediaContent?.icon ?? "location"}
              size={16}
              color="cadmium"
              className={s.icon}
            />
            {mediaContent?.location && (
              <Body size="uppercase-small" weight="semiBold">
                {mediaContent.location}
              </Body>
            )}
          </div>
        )}
      </Button>
    </article>
  );
};

MediaCard.displayName = "MediaCard";
